import React from "react";
import { api, baseUrl } from "../../api/config";
import { RecordContextType } from "../../components/record/Record";
import { getStore } from "../../store/store";
import { isClientLessThan18YearsOld } from "../../utils/getClientAge";
import { ROLES } from "../../utils/constants";
import { TreatmentAndPaymentInformation } from "../../components/treatment-and-payment-information/TreatmentAndPaymentInformation";
import { SMSDialog } from "../../components/sms-dialog/SMSDialog";
import { TreatmentPrescDocumentDialog } from "./actions-utils/TreatmentPrescDocumentDialog";
import { openGenerateInstrumentLinksDialog } from "./actions-utils/GenerateInstrumentLinksDialog";
import { SendAppDownloadEmailDialog } from "./actions-utils/SendAppDownloadEmailDialog";

const {
	recepcionist,
	reception_director,
	technical_director,
	neurofeedback_director,
	theurapist,
	nfb_techncians,
	callcenter,
	substituion_joana_temp,
	client_owner,
	back_office,
	tech_admin
} = ROLES;

export const actions = [
	{
		code: "contract_informed_consent_child",
		name: "Enviar consentimento informado - Menores",
		actionFn: (context: RecordContextType, code: string) => createAndSendContract(context, code),
		roles: [recepcionist, reception_director, substituion_joana_temp, back_office, technical_director]
	},
	{
		code: "contract_informed_consent_adult",
		name: "Enviar consentimento informado - Adultos",
		actionFn: (context: RecordContextType, code: string) => createAndSendContract(context, code),
		roles: [recepcionist, reception_director, substituion_joana_temp, back_office, technical_director]
	},
	{
		code: "data_for_research_child",
		name: "Enviar consentimento para investigação - Menores",
		actionFn: (context: RecordContextType, code: string) => createAndSendContract(context, code),
		roles: [recepcionist, reception_director, substituion_joana_temp, back_office, technical_director]
	},
	{
		code: "data_for_research_adult",
		name: "Enviar consentimento para investigação - Adultos",
		actionFn: (context: RecordContextType, code: string) => createAndSendContract(context, code),
		roles: [recepcionist, reception_director, substituion_joana_temp, back_office, technical_director]
	},
	{
		code: "ask_testimonial",
		name: "Enviar link para testemunho",
		actionFn: (context: RecordContextType) => askTestimonial(context),
		roles: [
			neurofeedback_director,
			technical_director,
			reception_director,
			theurapist,
			substituion_joana_temp,
			tech_admin,
			back_office
		]
	},
	{
		code: "send_payment_information_no_agreement",
		name: "Enviar informação de tratamento e pagamento - sem acordo/seguro",
		actionFn: (context: any) => openTreatmentAndPaymentInformationDialog(context, { agreement: false }),
		roles: [
			recepcionist,
			reception_director,
			callcenter,
			substituion_joana_temp,
			back_office,
			tech_admin,
			technical_director
		]
	},
	{
		code: "send_payment_information_with_agreement",
		name: "Enviar informação de tratamento e pagamento - com acordo/seguro",
		actionFn: (context: any) => openTreatmentAndPaymentInformationDialog(context, { agreement: true }),
		roles: [
			recepcionist,
			reception_director,
			callcenter,
			substituion_joana_temp,
			back_office,
			tech_admin,
			technical_director
		]
	},
	{
		code: "update_nfb_training_minutes",
		name: "Atualizar minutos de treino",
		actionFn: (context: any) => updateNfbTrainingMinutes(context),
		roles: [neurofeedback_director, nfb_techncians, theurapist, technical_director, client_owner]
	},
	{
		code: "send_sms",
		name: "Enviar SMS",
		actionFn: (context: any) => openSmsDialog(context),
		roles: [
			recepcionist,
			reception_director,
			callcenter,
			substituion_joana_temp,
			back_office,
			technical_director,
			tech_admin
		]
	},
	{
		code: "send_treatment_prescription",
		name: "Gerar/Enviar Prescrição de Tratamento",
		actionFn: (context: RecordContextType) => openTreatmentPrescriptionPdfDialog(context)
	},
	{
		code: "create_automatic_sessions",
		name: "Criar sessões automáticas",
		actionFn: (context: RecordContextType) => createAutomaticSessions(context),
		roles: [recepcionist, reception_director, technical_director, callcenter, substituion_joana_temp, back_office]
	},
	{
		code: "send_app_download_email",
		name: "Enviar Email de Download da App (novo)",
		actionFn: (context: RecordContextType) => openSendAppDownloadEmailDialog(context),
		roles: [
			recepcionist,
			reception_director,
			callcenter,
			substituion_joana_temp,
			back_office,
			tech_admin,
			technical_director
		]
	},
	{
		code: "generate_instrument_links",
		name: "Enviar Email com Instrumentos (novo)",
		actionFn: (context: RecordContextType) => openGenerateInstrumentLinksDialog(context),
		roles: [
			recepcionist,
			reception_director,
			callcenter,
			substituion_joana_temp,
			back_office,
			tech_admin,
			technical_director,
			neurofeedback_director,
			nfb_techncians,
			theurapist
		]
	}
];

const createAndSendContract = async (context: RecordContextType, code: string) => {
	if (!context.item.client.birthday) throw Error("Este cliente não tem data de nascimento definida");

	if (
		isClientLessThan18YearsOld(context.item.client.birthday) &&
		context.item.relative_resposible_for_treatment.length === 0
	)
		throw Error("Este cliente é menor de idade, por favor defina o responsável pelo tratamento");

	try {
		const response = await api({
			method: "POST",
			url: "https://hook.eu1.make.com/efw5elytixkcew4c4vzkt719fbfonirt",
			data: {
				item: context.item,
				contract: code
			}
		});
		return response.data;
	} catch (error: any) {
		throw new Error(error?.response?.data || error.response || "algo correu mal");
	}
};

const askTestimonial = async (context: RecordContextType) => {
	const confirmed = window.confirm("De certeza que deseja enviar o link para testemunho?");
	const { notifications } = getStore();
	if (!confirmed) return;

	try {
		const response = await api({
			method: "POST",
			url: "https://hook.eu1.make.com/1yaey5zj7aa495ncsbo30yrc1gcneu2l",
			data: {
				item: context.item
			}
		});
		notifications.setNotification({
			message: "Link enviado com sucesso",
			severity: "success",
			isOpen: true
		});
		return response.data;
	} catch (error: any) {
		notifications.setNotification({
			message: error?.response?.data || "algo correu mal",
			severity: "error",
			isOpen: true
		});
	}
};

const openTreatmentAndPaymentInformationDialog = (context: RecordContextType, options: { agreement: boolean }) => {
	try {
		const { appDialog } = getStore();

		appDialog.createDialog({
			title: "Enviar informação de tratamento e pagamento",
			contentAndActions: React.createElement(TreatmentAndPaymentInformation, {
				context: context,
				agreement: options.agreement,
				onClose: () => appDialog.toggleIsOpen()
			}),
			allowSoftCancel: true
		});
	} catch (error: any) {
		throw new Error("algo correu mal");
	}
};

const updateNfbTrainingMinutes = async (context: RecordContextType) => {
	const { notifications } = getStore();

	try {
		const response = await api({
			method: "POST",
			url: "https://hook.eu1.make.com/50njenr53qq753crq8eyqirldiv7ho7i",
			data: {
				item: context.item
			}
		});
		notifications.setNotification({
			message:
				"Minutos de treino atualizados com sucesso (faz refresh na página para ver os minutos atualizados)",
			severity: "success",
			isOpen: true
		});
		return response.data;
	} catch (error: any) {
		notifications.setNotification({
			message: error?.response?.data || "algo correu mal",
			severity: "error",
			isOpen: true
		});
	}
};

const openSmsDialog = (context: RecordContextType) => {
	try {
		const { appDialog } = getStore();

		appDialog.createDialog({
			title: "Enviar SMS",
			contentAndActions: React.createElement(SMSDialog, {
				context: { ...context, entity: "treatments" },
				onClose: () => appDialog.toggleIsOpen()
			}),
			allowSoftCancel: true
		});

		return {
			showSuccessMessage: "no"
		};
	} catch (error: any) {
		throw new Error("algo correu mal");
	}
};

const openTreatmentPrescriptionPdfDialog = async (context: RecordContextType) => {
	const { appDialog } = getStore();

	appDialog.createDialog({
		title: "Prescrição de Tratamento",
		contentAndActions: React.createElement(TreatmentPrescDocumentDialog, {
			context: context,
			onClose: () => appDialog.toggleIsOpen()
		})
	});
	return {
		showSuccessMessage: "no"
	};
};

const openSendAppDownloadEmailDialog = (context: RecordContextType) => {
	const { appDialog } = getStore();

	appDialog.createDialog({
		title: "Enviar email de download da app",
		contentAndActions: React.createElement(SendAppDownloadEmailDialog, {
			context: context,
			onClose: () => appDialog.toggleIsOpen()
		}),
		allowSoftCancel: true
	});

	return {
		showSuccessMessage: "no"
	};
};

const createAutomaticSessions = async (context: RecordContextType) => {
	const confirmed = window.confirm("Tem a certeza que deseja criar sessões automáticas?");
	if (!confirmed) return;

	const treatmentId = context.item?.id;
	if (!treatmentId) throw Error("Este tratamento não tem ID definido");

	const { notifications } = getStore();

	try {
		const response = await api({
			method: "POST",
			url: `${baseUrl}/auto-book-sessions/trigger`,
			data: { treatment_ids: [treatmentId] }
		});

		const { sessionsCreated, sessionsSkipped, cancelledSessionsSkipped, rescheduledSessionsSkipped } =
			response.data;
		notifications.setNotification({
			message: `Sessões criadas: ${sessionsCreated}\nSessões ignoradas:\n- Já criadas: ${sessionsSkipped}\n- Estavam canceladas: ${cancelledSessionsSkipped}\n- Foram remarcadas: ${rescheduledSessionsSkipped}`,
			severity: "success",
			isOpen: true
		});
	} catch (error: any) {
		notifications.setNotification({
			message: error?.response?.data?.error || "Algo correu mal",
			severity: "error",
			isOpen: true
		});
	}
};
